import React from 'react';

export interface PillItem {
  key: string;
  label: string;
  /** Small trailing marker, e.g. a count or a dirty dot. */
  badge?: React.ReactNode;
  disabled?: boolean;
}

/**
 * Horizontally scrolling pill row used as the in-page section switcher on mobile.
 * Scrolls the active pill into view so a selection made off-screen (by code, e.g.
 * jumping to Levels after a save) is still visible.
 */
const SectionPills: React.FC<{
  items: PillItem[];
  activeKey: string;
  onChange: (key: string) => void;
  /** Rendered pinned at the right edge, outside the scroll area (e.g. a chart toggle). */
  extra?: React.ReactNode;
  /** Stick to the top of the scroll container as the content above it scrolls past. */
  sticky?: boolean;
}> = ({ items, activeKey, onChange, extra, sticky }) => {
  const railRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    const el = railRef.current?.querySelector<HTMLElement>(`[data-pill="${activeKey}"]`);
    // jsdom has no scrollIntoView, and it is only a nicety anyway.
    el?.scrollIntoView?.({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
  }, [activeKey]);

  return (
    <div
      style={{
        flexShrink: 0,
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        // Opaque: the chart scrolls underneath this when it is sticky.
        background: '#15181d',
        borderBottom: '1px solid #2B3139',
        padding: '6px 8px',
        ...(sticky ? { position: 'sticky' as const, top: 0, zIndex: 5 } : null),
      }}
    >
      <div
        ref={railRef}
        className="fbc-pill-rail"
        role="tablist"
        style={{
          display: 'flex',
          gap: 6,
          overflowX: 'auto',
          flex: 1,
          minWidth: 0,
          scrollbarWidth: 'none',
        }}
      >
        {items.map((item) => {
          const active = item.key === activeKey;
          return (
            <button
              key={item.key}
              data-pill={item.key}
              role="tab"
              aria-selected={active}
              disabled={item.disabled}
              onClick={() => onChange(item.key)}
              style={{
                flexShrink: 0,
                height: 30,
                padding: '0 14px',
                borderRadius: 15,
                fontSize: 12.5,
                fontWeight: 600,
                whiteSpace: 'nowrap',
                cursor: item.disabled ? 'not-allowed' : 'pointer',
                opacity: item.disabled ? 0.4 : 1,
                background: active ? '#0ECB81' : '#23272e',
                color: active ? '#0B0E11' : '#B7BDC6',
                border: `1px solid ${active ? '#0ECB81' : '#2B3139'}`,
                WebkitTapHighlightColor: 'transparent',
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
              }}
            >
              {item.label}
              {item.badge}
            </button>
          );
        })}
      </div>
      {extra}
    </div>
  );
};

export default SectionPills;
